import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SuggestionChip } from "../SuggestionChip";
import type { AnswerValue, QuestionProps } from "../types";
import { acceptSuggestion } from "./suggestion";
import { Badge } from "@/components/ui/badge";

/**
 * Shared frame for every question: title (as `<label>` or group label), help, badges,
 * suggestion chip and the control. `asGroup` is for radio and checkbox groups.
 */
export function QuestionShell({
  props,
  inputId,
  asGroup,
  children,
  error,
}: {
  props: QuestionProps;
  inputId: string;
  asGroup?: boolean;
  children: (ids: { labelId: string; helpId?: string; errorId?: string }) => ReactNode;
  error?: string | null;
}) {
  const { t } = useTranslation();
  const { question, flags, disabled, onChange, answeredBy } = props;
  const labelId = `${inputId}-label`;
  const helpId = question.help ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const suggested: AnswerValue | undefined = question.our_recommendation
    ? acceptSuggestion(question, question.our_recommendation)
    : undefined;

  const titleClass = "font-heading text-lg font-semibold text-heading";
  const required = question.required ? (
    <span className="text-sm font-normal text-fg-muted">{t("questionnaire.required")}</span>
  ) : null;

  return (
    <div data-question-id={question.id} className="flex flex-col gap-3 border-b border-border py-6 last:border-b-0">
      {flags.isNew || flags.needsReview ? (
        <div className="flex gap-2">
          {flags.isNew ? <Badge variant="solid">{t("questionnaire.badgeNew")}</Badge> : null}
          {flags.needsReview ? <Badge variant="warning">{t("questionnaire.badgeChanged")}</Badge> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {asGroup ? (
          <span id={labelId} className={titleClass}>
            {question.title}
          </span>
        ) : (
          <label id={labelId} htmlFor={inputId} className={titleClass}>
            {question.title}
          </label>
        )}
        {required}
      </div>
      {question.help ? (
        <p id={helpId} className="-mt-1 text-fg-muted">
          {question.help}
        </p>
      ) : null}
      {question.our_recommendation ? (
        <SuggestionChip
          text={question.our_recommendation}
          onAccept={!disabled && suggested !== undefined ? () => onChange(suggested) : undefined}
        />
      ) : null}
      {children({ labelId, helpId, errorId })}
      {answeredBy ? <p className="text-sm text-fg-muted">{t("questionnaire.answeredBy", { name: answeredBy })}</p> : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
